import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  filter,
  map,
  merge,
  Observable,
  of,
  switchMap,
  take,
  tap
} from 'rxjs';

import {
  AGE_LIST,
  FilterOptionsStore,
  IAge,
  IFilterFormOptionValue,
  IGenderStatic,
  IModality,
  ISpecialtyStatic,
  ITreatmentPreference,
  LANGUAGE_LIST,
  LANGUAGE_LIST_TOP_COUNT,
  MODALITY_LIST,
  SERVICE_TYPE_LIST,
  SESSION_TYPES,
  SPECIALTY_LIST,
  TREATMENT_PREFERENCE_LIST
} from '@sondermind/data-access/public-facing/filter-form';
import {
  CASH_PAY_OPTION_VALUE,
  IInsurance,
  IInsuranceOptionValue,
  InsuranceCategoryName,
  InsuranceType,
  NoInsuranceOptionValue
} from '@sondermind/utilities/models-provider-network-company';
import { USStateOptions } from '@sondermind/utilities/states-list';
import { IFacetBucket, ProviderSearchHttpService } from '@sondermind/data-access/public-facing/provider-search';
import { DropdownOption } from '@sondermind/iris/dropdown';
import { Logger } from '@sondermind/datadog-logging';
import { FilterCategory } from '../models/filter-name.constants';
import { IOptionCategory } from '../models/option-category.interface';

export interface IAllFilterOptions {
  stateOptions: Array<IOptionCategory<string>>;
  insuranceOptions: Array<IOptionCategory<IInsuranceOptionValue>>;
  specialtyOptions: Array<IOptionCategory<IFilterFormOptionValue>>;
  modalityOptions: Array<IOptionCategory<IFilterFormOptionValue>>;
  genderOptions: Array<IOptionCategory<IFilterFormOptionValue>>;
  sessionFormatOptions: Array<IOptionCategory<IFilterFormOptionValue>>;
  ageOptions: Array<IOptionCategory<IFilterFormOptionValue>>;
  treatmentPreferenceOptions: Array<IOptionCategory<IFilterFormOptionValue>>;
  languageOptions: Array<IOptionCategory<IFilterFormOptionValue>>;
  serviceTypeOptions: Array<IOptionCategory<IFilterFormOptionValue>>;
}

/** One admin-only option list, loaded from a provider-index facet for the searched state. */
export interface IFacetFilterOptions {
  category: FilterCategory;
  options: Array<IOptionCategory<IFilterFormOptionValue>>;
}

/** A provider-index facet that fills one admin-only filter's option list. */
interface IFacetBackedFilter {
  category: FilterCategory;
  facetField: string;
  /** Bucket to dropdown option; null drops the bucket from the list. */
  toOption: (bucket: IFacetBucket) => DropdownOption<IFilterFormOptionValue> | null;
}

/** Licensure names arrive state-prefixed ("CO - Licensed Clinical Social Worker"). */
const STATE_PREFIX = /^[A-Za-z]{2}\s*-\s*/;

/** A declined answer is not a race/ethnicity, so it is not offered as a filter. */
const ETHNICITY_TAGS_NOT_OFFERED: ReadonlyArray<string> = ['preferNotToSay'];

/** The bucket key is the reference-data tag, which is also what /search matches on. */
function referenceDataOption(bucket: IFacetBucket): DropdownOption<IFilterFormOptionValue> {
  const title = bucket.attributes?.['title'] || bucket.value;
  return { value: { title, tag: bucket.value }, name: title };
}

/** Every facet only returns values a live provider in the searched state holds. */
const FACET_BACKED_FILTERS: ReadonlyArray<IFacetBackedFilter> = [
  {
    category: FilterCategory.LICENSURE,
    facetField: 'licensures',
    toOption: (bucket) => {
      // Bracket access: several consumer libs set `noPropertyAccessFromIndexSignature`.
      const label = bucket.attributes?.['name']?.replace(STATE_PREFIX, '') || bucket.value;
      return { value: { title: label, tag: bucket.value }, name: `${label} (${bucket.value})` };
    }
  },
  {
    category: FilterCategory.ETHNICITY,
    facetField: 'ethnicities',
    toOption: (bucket) => (
      ETHNICITY_TAGS_NOT_OFFERED.includes(bucket.value) ? null : referenceDataOption(bucket)
    )
  }
];

@Injectable({
  providedIn: 'root'
})
export class FilterLoadingService {
  private filterError = new BehaviorSubject<boolean>(false);
  readonly filterError$ = this.filterError.asObservable();

  private allFilterOptions = new BehaviorSubject<IAllFilterOptions | null>(null);
  readonly allFilterOptions$ = this.allFilterOptions.asObservable();
  private lastLoadedState: string | null = null;
  private lastLoadedIsPsychiatrist: boolean = false;

  constructor(
    private filtersStore: FilterOptionsStore,
    private providerSearchHttpService: ProviderSearchHttpService,
    private logger: Logger
  ) {}

  /** Returns cached options when state and psychiatrist flag are unchanged; refreshes insurance otherwise. */
  getAllFilterOptions$(): Observable<IAllFilterOptions | null> {
    const currentState = this.filtersStore.getCurrentState();
    const isCurrentPsychiatrist = this.filtersStore.getCurrentIsPsychiatrist();

    const cachedOptions = this.allFilterOptions.value;
    if (
      cachedOptions &&
      this.lastLoadedState === currentState
    ) {
      if (this.lastLoadedIsPsychiatrist !== isCurrentPsychiatrist) {
        return this.getInsuranceOptions$().pipe(
          map((insuranceOptions) => ({ ...cachedOptions, insuranceOptions })),
          tap((filterOptions) => {
            this.allFilterOptions.next(filterOptions);
            this.lastLoadedIsPsychiatrist = this.filtersStore.getCurrentIsPsychiatrist();
          }),
        );
      }
      return of(cachedOptions);
    }
    return this.loadFilterOptions$(currentState);
  }

  /** Bypasses the cache. */
  reloadFilterOptions$(): Observable<IAllFilterOptions | null> {
    return this.loadFilterOptions$(this.filtersStore.getCurrentState());
  }

  /**
   * Emits each admin-only facet list as it lands so one slow facet never holds back the others.
   * A failure emits an empty list, not filterError$: losing one dropdown must not take down the directory.
   */
  getFacetOptions$(state: string): Observable<IFacetFilterOptions> {
    return merge(...FACET_BACKED_FILTERS.map((spec) => this.loadFacetOptions$(state, spec)));
  }

  private loadFacetOptions$(state: string, spec: IFacetBackedFilter): Observable<IFacetFilterOptions> {
    return this.providerSearchHttpService.providerIndexFacets$({ state, facetField: spec.facetField }).pipe(
      map((response) => {
        const options = (response?.buckets ?? [])
          .map((bucket) => spec.toOption(bucket))
          .filter((option): option is DropdownOption<IFilterFormOptionValue> => option !== null);
        return { category: spec.category, options: [{ name: spec.category, options }] };
      }),
      catchError((error: unknown) => {
        this.logger.warn('Could not load facet-backed filter options', {
          state,
          facetField: spec.facetField,
          httpStatus: (error as { status?: number })?.status
        });
        return of({ category: spec.category, options: [] });
      })
    );
  }

  private loadFilterOptions$(stateForCache: string | null): Observable<IAllFilterOptions | null> {
    this.filterError.next(false);

    return this.filtersStore.isStateLoaded$().pipe(
      filter((isStateLoaded) => !!isStateLoaded),
      take(1),
      switchMap(() => combineLatest({
        stateOptions: this.getStateOptions$(),
        insuranceOptions: this.getInsuranceOptions$(),
        specialtyOptions: this.getSpecialtyOptions$(),
        modalityOptions: this.getModalityOptions$(),
        genderOptions: this.getGenderOptions$(),
        sessionFormatOptions: this.getSessionFormatOptions$(),
        ageOptions: this.getAgeOptions$(),
        treatmentPreferenceOptions: this.getTreatmentPreferenceOptions$(),
        languageOptions: this.getLanguageOptions$(),
        serviceTypeOptions: this.getServiceTypeOptions$()
      })),
      tap((filterOptions) => {
        this.allFilterOptions.next(filterOptions);
        this.lastLoadedState = stateForCache;
        this.lastLoadedIsPsychiatrist = this.filtersStore.getCurrentIsPsychiatrist();
      }),
      catchError(() => {
        this.filterError.next(true);
        return of(null);
      })
    );
  }

  private getStateOptions$(): Observable<Array<IOptionCategory<string>>> {
    return of(USStateOptions).pipe(
      map((list) => list
        .filter((opt) => opt.activeProviderState)
        .sort((a, b) => a.label.localeCompare(b.label))
      ),
      map((active) => [
        {
          name: FilterCategory.STATE,
          options: active.map((opt) => ({
            value: opt.value,
            name: opt.label,
          })),
        },
      ]),
      catchError(() => {
        this.filterError.next(true);
        return of([]);
      })
    );
  }

  private getServiceTypeOptions$(): Observable<Array<IOptionCategory<IFilterFormOptionValue>>> {
    return of([{
      name: FilterCategory.THERAPY_OR_PSYCHIATRY,
      options: SERVICE_TYPE_LIST.map(({ name, value }) => ({ name, value: { ...value } }))
    }]);
  }

  getInsuranceOptions$(): Observable<Array<IOptionCategory<IInsuranceOptionValue>>> {
    return this.filtersStore.insuranceList$().pipe(
      map((array: IInsurance[]) => {
        const insurance: IOptionCategory<IInsuranceOptionValue> = {
          name: InsuranceCategoryName.INSURANCE,
          options: []
        };
        const medicare: IOptionCategory<IInsuranceOptionValue> = {
          name: InsuranceCategoryName.MEDICARE,
          options: []
        };
        const medicaid: IOptionCategory<IInsuranceOptionValue> = {
          name: InsuranceCategoryName.MEDICAID,
          options: []
        };
        const eap: IOptionCategory<IInsuranceOptionValue> = {
          name: InsuranceCategoryName.EAP,
          options: []
        };

        const noInsuranceOptions: IOptionCategory<IInsuranceOptionValue> = {
          name: '',
          options: [{
            name: 'I\'ll pay out of pocket',
            value: CASH_PAY_OPTION_VALUE
          }, {
            name: 'I\'m not sure',
            value: {
              pncId: NoInsuranceOptionValue.NOT_SURE,
              title: 'not sure',
              type: InsuranceType.CASH,
              tag: ''
            }
          }]
        };

        array.forEach((arrayMember: IInsurance) => {
          // One insurance may belong to several categories.
          if (arrayMember.isMedicare) {
            medicare.options.push({
              value: {
                pncId: arrayMember.pncId,
                title: arrayMember.title,
                type: InsuranceType.MEDICARE,
                tag: arrayMember.tag
              },
              name: arrayMember.title
            });
          }
          if (arrayMember.isMedicaid) {
            medicaid.options.push({
              value: {
                pncId: arrayMember.pncId,
                title: arrayMember.title,
                type: InsuranceType.MEDICAID,
                tag: arrayMember.tag
              },
              name: arrayMember.title
            });
          }
          if (arrayMember.isEap) {
            eap.options.push({
              value: {
                pncId: arrayMember.pncId,
                title: arrayMember.title,
                type: InsuranceType.EAP,
                tag: arrayMember.tag
              },
              name: arrayMember.title
            });
          }

          if (arrayMember.isPrivateInsurance) {
            insurance.options.push({
              value: {
                pncId: arrayMember.pncId,
                title: arrayMember.title,
                type: InsuranceType.INSURANCE,
                tag: arrayMember.tag
              },
              name: arrayMember.title
            });
          }
        });

        return [noInsuranceOptions, medicare, medicaid, insurance, eap];
      }),
      catchError(() => {
        this.filterError.next(true);
        return of([]);
      })
    );
  }

  getSpecialtyOptions$(): Observable<Array<IOptionCategory<IFilterFormOptionValue>>> {
    return of(SPECIALTY_LIST).pipe(
      map((specialties: ISpecialtyStatic[]) => {
        const specialtiesSortedByTopSelection = [...specialties].sort(
          (s1, s2) => Number(s2.isTopSelection) - Number(s1.isTopSelection)
        );

        return [{
          name: FilterCategory.SPECIALTY,
          options: specialtiesSortedByTopSelection.map((arrayMember: ISpecialtyStatic) => ({
            value: {
              title: arrayMember.title,
              tag: arrayMember.tag
            },
            name: arrayMember.title
          }))
        }];
      }),
      catchError(() => {
        this.filterError.next(true);
        return of([]);
      })
    );
  }

  private getModalityOptions$(): Observable<Array<IOptionCategory<IFilterFormOptionValue>>> {
    return of(MODALITY_LIST).pipe(
      map((modalities: IModality[]) => [{
        name: FilterCategory.TYPE_OF_SERVICE,
        options: [
          ...modalities.map((arrayMember: IModality) => ({
            value: {
              title: arrayMember.title,
              tag: arrayMember.tag
            },
            name: arrayMember.title
          }))
        ]
      }]),
      catchError(() => {
        this.filterError.next(true);
        return of([]);
      })
    );
  }

  private getGenderOptions$(): Observable<Array<IOptionCategory<IFilterFormOptionValue>>> {
    return this.filtersStore.genderList$().pipe(
      map((array: IGenderStatic[]) => [{
        name: FilterCategory.GENDER_PREFERENCE,
        options: array.map((arrayMember: IGenderStatic) => ({
          value: {
            title: arrayMember.title,
            tag: arrayMember.tag
          },
          name: arrayMember.title
        }))
      }]),
      catchError(() => {
        this.filterError.next(true);
        return of([]);
      })
    );
  }

  private getSessionFormatOptions$(): Observable<Array<IOptionCategory<IFilterFormOptionValue>>> {
    const sessionFormatOptions = [{
      name: FilterCategory.SESSION_FORMAT,
      options: [
        {
          value: {
            title: SESSION_TYPES.online.title,
            tag: SESSION_TYPES.online.tag
          },
          name: SESSION_TYPES.online.title
        },
        {
          value: {
            title: SESSION_TYPES.inPerson.title,
            tag: SESSION_TYPES.inPerson.tag
          },
          name: SESSION_TYPES.inPerson.title
        }
      ]
    }];

    return of(sessionFormatOptions);
  }

  private getAgeOptions$(): Observable<Array<IOptionCategory<IFilterFormOptionValue>>> {
    return of(AGE_LIST).pipe(
      map((array: IAge[]) => [{
        name: FilterCategory.AGE_GROUP_TREATED,
        options: array.map((arrayMember: IAge) => ({
          value: {
            title: arrayMember.title,
            tag: arrayMember.tag
          },
          name: arrayMember.title
        }))
      }]),
      catchError(() => {
        this.filterError.next(true);
        return of([]);
      })
    );
  }

  private getTreatmentPreferenceOptions$(): Observable<Array<IOptionCategory<IFilterFormOptionValue>>> {
    return of(TREATMENT_PREFERENCE_LIST).pipe(
      map((preferences: ITreatmentPreference[]) => [{
        name: FilterCategory.TREATMENT_APPROACH,
        options: preferences.map((arrayMember: ITreatmentPreference) => ({
          value: {
            title: arrayMember.title,
            tag: arrayMember.tag
          },
          name: arrayMember.title
        })),
        trimmedListCount: 6
      }]),
      catchError(() => {
        this.filterError.next(true);
        return of([]);
      })
    );
  }

  private getLanguageOptions$(): Observable<Array<IOptionCategory<IFilterFormOptionValue>>> {
    return of([{
      name: FilterCategory.LANGUAGE,
      options: LANGUAGE_LIST.map((language) => ({ value: { ...language }, name: language.title })),
      trimmedListCount: LANGUAGE_LIST_TOP_COUNT
    }]);
  }
}
